import { useContext } from 'react';
import {
  Collapse, List, Radio, Button,
} from 'antd';
import {
  number, func, object, string, bool, array,
} from 'prop-types';

import { ChevronSVG } from 'components/iconsSVG/chevron';
import { filterGrid, sorts } from 'utils/filters';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { checkOrderFilter } from 'utils/helpers';
import { i18nContext } from 'context/i18n';
import { FilterTag } from 'components/filterTag';

import styles from './style.module.css';

const { Panel } = Collapse;

export const filterColors = {
  'Black': '#000000', // black
  'Negro': '#000000',
  'White': '#fff', // white
  'Blanco': '#FFFFFF',
  'Purple': '#800080', // purple
  'Violeta': '#800080', // purple
  'Pink': '#f892d6', // pink
  'Rosa': '#f892d6', // pink
  'Burdeos': '#8e0000', // burdeos
  'Burgundy': '#8e0000', // burdeos
  'Red': '#db2415', // red
  'Rojo': '#db2415', // red
  'Blue': '#2801f6', // blue
  'Azul': '#2801f6', // blue
  'Orange': '#ff7000', // orange
  'Naranja': '#ff7000', // orange
  'Beige': '#e9d8cc', // beige
  'Green': '#18b000', // green
  'Verde': '#18b000', // green
  'Brown': '#783600', // brown
  'Marron': '#783600', // brown
  'Yellow': '#fbe401', // yellow
  'Amarillo': '#fbe401', // yellow
  'Grey': '#bcb9b9', // grey
  'Gris': '#bcb9b9', // grey
  'Multicolor': 'linear-gradient(0deg, rgba(34,193,195,1) 0%, rgba(253,187,45,1) 100%)', // multicolor
  'Nude': '#f3d3b7', // nude
  'Brown grey': '#3d3635',
  'Marrón grisaceo': '#3d3635',
};

export default function FilterPanelSearcher({
  filtersCount, onApply, currentSort, availableFilters, baseAvailable, appliedFilters, onClickCloseBtn, camperlab, facets, lang,
}) {
  const { t } = useContext(i18nContext);
  const unisexFilter = 'en_target_uFilter:Unisex';
  const genderFilters = [ 'en_target_uFilter:Men', 'en_target_uFilter:Women' ];
  const unisexKidsFilter = 'en_target_uFilter:Kids-Unisex';
  const kidsFilters = [ 'en_target_uFilter:Girls', 'en_target_uFilter:Boys' ];
  const handleClickFilter = (filter) => {
    let currentFilters = [ ...appliedFilters ];
    const filterType = filter.split(':').shift().replace('_uFilter', '').replace('en_', '');
    const filterValue = filter.split(':').pop().toLowerCase();
    if (!currentFilters.includes(filter)) {
      currentFilters.push(filter);
      if (genderFilters.includes(filter) && !currentFilters.includes(unisexFilter)) {
        currentFilters.push(unisexFilter);
      }
      if (kidsFilters.includes(filter) && !currentFilters.includes(unisexKidsFilter)) {
        currentFilters.push(unisexKidsFilter);
      }
      const cumulatedFilters = {};
      currentFilters.map((appliedFilter) => {
        const appliedFilterType = appliedFilter.split(':').shift().replace('_uFilter', '').replace('en_', '');
        const appliedFilterValue = appliedFilter.split(':').pop().toLowerCase();
        if (appliedFilterType in cumulatedFilters) {
          cumulatedFilters[appliedFilterType] = `${cumulatedFilters[appliedFilterType]}|${appliedFilterValue}`;
        } else {
          cumulatedFilters[appliedFilterType] = appliedFilterValue;
        }
        return null;
      });
      dataLayerHandleEvent({
        event: 'filter_item',
        event_category: 'filters',
        event_detail_1: filterType || '',
        event_detail_2: filterValue || '',
        event_detail_3: 'add',
        cumulated_filters: cumulatedFilters,
      });
    } else {
      currentFilters = currentFilters.filter((item) => item !== filter);
      if (genderFilters.includes(filter) && (!currentFilters.includes(genderFilters[0]) || !currentFilters.includes(genderFilters[1])) && currentFilters.includes(unisexFilter)) {
        currentFilters = currentFilters.filter((item) => item !== unisexFilter);
      }
      if (kidsFilters.includes(filter) && (!currentFilters.includes(kidsFilters[0]) || !currentFilters.includes(kidsFilters[1])) && currentFilters.includes(unisexKidsFilter)) {
        currentFilters = currentFilters.filter((item) => item !== unisexKidsFilter);
      }
      const cumulatedFilters = {};
      currentFilters.map((appliedFilter) => {
        const appliedFilterType = appliedFilter.split(':').shift().replace('_uFilter', '').replace('en_', '');
        const appliedFilterValue = appliedFilter.split(':').pop().toLowerCase();
        if (appliedFilterType in cumulatedFilters) {
          cumulatedFilters[appliedFilterType] = `${cumulatedFilters[appliedFilterType]}|${appliedFilterValue}`;
        } else {
          cumulatedFilters[appliedFilterType] = appliedFilterValue;
        }
        return null;
      });
      dataLayerHandleEvent({
        event: 'filter_item',
        event_category: 'filters',
        event_detail_1: '',
        event_detail_2: '',
        event_detail_3: 'remove',
        cumulated_filters: cumulatedFilters,
      });
    }
    onApply(currentFilters, currentSort);
  };

  const handleClickSort = (sortValue) => {
    dataLayerHandleEvent({
      event: 'eventGA',
      eventCat: 'ecommerce',
      eventAct: 'gridSort',
      eventLbl: sortValue,
    });
    const cumulatedFilters = {};
    appliedFilters.map((appliedFilter) => {
      const appliedFilterType = appliedFilter.split(':').shift().replace('_uFilter', '').replace('en_', '');
      const appliedFilterValue = appliedFilter.split(':').pop().toLowerCase();
      if (appliedFilterType in cumulatedFilters) {
        cumulatedFilters[appliedFilterType] = `${cumulatedFilters[appliedFilterType]}|${appliedFilterValue}`;
      } else {
        cumulatedFilters[appliedFilterType] = appliedFilterValue;
      }
      return null;
    });
    cumulatedFilters.sort_by = sortValue;
    dataLayerHandleEvent({
      event: 'filter_item',
      event_category: 'filters',
      event_detail_1: 'sort by',
      event_detail_2: sortValue,
      event_detail_3: 'add',
      cumulated_filters: cumulatedFilters,
    });
    onApply(appliedFilters, sortValue);
  };

  const handleReset = () => {
    dataLayerHandleEvent({
      event: 'filter_item',
      event_category: 'filters',
      event_detail_1: '',
      event_detail_2: '',
      event_detail_3: 'remove',
      cumulated_filters: {},
    });
    onApply({}, currentSort);
  };

  const responseFilters = Object.keys(baseAvailable).filter((responseFilter) => baseAvailable[responseFilter].length > 0);

  checkOrderFilter(responseFilters);

  const filtersArray = facets?.text?.list || [];
  const filterColor = /(en|es)_color_uFilter/;
  return (
    <>
      <Collapse
        bordered={false}
        defaultActiveKey={[ `${lang}_target_uFilter` ]}
        expandIcon={({ isActive }) => <span className={isActive ? styles.chevronActive : ''}><ChevronSVG /></span>}
        expandIconPosition="right"
        className={!camperlab ? styles.filterCollapse : `${styles.filterCollapse} ${styles.camperlab}`}
      >
        {filtersCount > 0 &&
          <Panel
            showArrow={false}
            collapsible="disabled"
            key='filtersApplied'
            className={styles.filtersAppliedPanel}
            header={
              <div className={`${styles.appliedFiltersPanel} ${styles.customFilterItem}`}>
                {appliedFilters.map((filterKey) => {
                  const label = filterKey.split(':').pop();
                  if (label === 'Kids-Unisex') {
                    return null;
                  }
                  return (
                    <FilterTag
                      key={filterKey}
                      onClick={() => handleClickFilter(filterKey)}
                      color={filterColor.test(filterKey) && label in filterColors ? filterColors[label] : null}
                      label={label}
                      checked={true}
                    />
                  );
                })}
              </div>
            }
          />
        }
        <Panel header={t('menu.lateral', 'ordenar.por')} key='sortby' className={styles.panelSort}>
          {
          Object.keys(sorts).map((sortValue) => (
            <Radio
              onClick={() => handleClickSort(sortValue)}
              key={sortValue}
              className={styles.sortRadio}
              checked={currentSort === sortValue}
            >
              {t('listado.productos', sorts[sortValue])}
            </Radio>
          ))}
        </Panel>
        {
          filtersArray && filtersArray.length && filtersArray.filter((facet) => facet.values?.length > 2).map(({
            displayName: title, filterField: id, values,
          }) => {
            const filterValues = values.filter((filterVal, index) => index % 2 === 0 && filterVal !== 'Unisex' && filterVal !== 'Kids-Unisex' && availableFilters.includes(`${id}:${filterVal}`));
            const isTarget = id.includes('target');
            let grid = filterGrid[id] || filterGrid.default;
            if (isTarget) {
              grid = filterGrid.targets;
            }
            return (
              <Panel
                key={id}
                header={title !== 'Size' ? title : t('menu.lateral', 'talla', 'Size')}
                className={`${title === 'Size' ? styles.sizesPanel : ''}`}
              >
                <List
                  grid={grid}
                  dataSource={filterValues}
                  locale={{ emptyText: 'No available filters' }}
                  renderItem={(filter) => {
                    const filterValue = `${id}:${filter}`;
                    const filterIsAvailable = availableFilters.includes(filterValue);
                    const checked = appliedFilters.includes(filterValue);
                    // const className = tagClassNames[id] || tagClassNames.default;
                    // const styleStr = `${className !== '' ? styles[className] : ''} ${filterIsAvailable ? '' : styles['non-interactive']}`;
                    if (id.includes('color')) {
                      console.log({
                        id, filterColor, filterColors, filter, test: filterColor.test(id),
                      });
                    }
                    return (
                      <List.Item>
                        <div className={styles.customFilterItem}>
                          <FilterTag
                            onClick={() => handleClickFilter(filterValue, !checked)}
                            color={filterColor.test(id) && filter in filterColors ? filterColors[filter] : null}
                            label={filter}
                            type={filter}
                            checked={checked}
                            available={filterIsAvailable}
                          />
                          {/* <CheckableTag
                            className={styleStr}
                            key={filter}
                            checked={checked}
                            onChange={() => handleClickFilter(filterValue, !checked)}
                            disabled={false}
                            >
                            {id === filterColor && filter in filterColors &&
                              <Badge color={filterColors[filter]} />
                            }
                            <span className={id !== filterColor ? '' : styles.colorTagLabel}>{filter}</span>
                            {checked &&
                              <span className={styles.closeMarkSpan}><CloseSVG/></span>
                            }
                          </CheckableTag> */}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </Panel>);
          })
        }
        <Panel
          showArrow={false}
          className={styles.panelFooter}
          collapsible="disabled"
          header={
          <div className={styles.filterPanelFooter}>
            <Button onClick={() => onClickCloseBtn(false)}>{t('generico', 'boton.cerrar', 'Close')}</Button>
            <Button onClick={handleReset}>{t('generico', 'boton.reiniciar', 'Reset')}</Button>
            <Button type="primary" onClick={() => onClickCloseBtn(false, true)}>{t('generico', 'boton.aplicar', 'Apply')}</Button>
          </div>
        } />
      </Collapse>
    </>
  );
}

FilterPanelSearcher.propTypes = {
  filtersCount: number,
  onApply: func.isRequired,
  availableFilters: array,
  baseAvailable: object.isRequired,
  appliedFilters: array,
  facets: object,
  onClickCloseBtn: func,
  currentSort: string,
  country: string,
  lang: string,
  camperlab: bool,
};
